import { useEffect } from 'preact/hooks';
import { API_BASE_URL, VAPID_PUBLIC_KEY } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { isIOS, isStandaloneDisplay } from '../pwa/platform.js';
import { needsRepair, repairSubscription } from '../pwa/pushRegistration.js';

/**
 * 197 (contracts/push-repair.md, research.md §1/§8): module-level, not component state — "once
 * per app session" (FR-021) means once for the life of this module, surviving remounts, not once
 * per mount. Reset only by a fresh page load (or, in tests, `vi.resetModules()`).
 */
let attemptedThisSession = false;

/**
 * Silently repairs a device's push registration when the currently published VAPID key has
 * moved out from under it. Mounted once in the shell (`main.tsx`), keyed on `AuthContext`'s
 * `isAuthenticated` so a sign-in that happens after mount still gets exactly one attempt.
 *
 * 🔴 Never destroys a registration without positive evidence it is broken — `indeterminate` does
 * nothing here, unlike the explicit "Enable" path (FR-006), which may act on doubt because the
 * customer asked (research.md §2).
 */
export function usePushRegistrationRepair(): void {
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (attemptedThisSession) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (isIOS() && !isStandaloneDisplay()) return;
    if (Notification.permission !== 'granted') return;
    if (loading || !isAuthenticated) return;

    let cancelled = false;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (cancelled) return;

        const result = needsRepair(
          subscription
            ? { applicationServerKey: subscription.options?.applicationServerKey }
            : null,
          VAPID_PUBLIC_KEY,
        );
        if (result !== 'superseded') return;
        if (cancelled) return;

        const accessToken = localStorage.getItem('access_token') ?? '';
        await repairSubscription(subscription as PushSubscription, registration, VAPID_PUBLIC_KEY, {
          apiBaseUrl: API_BASE_URL,
          accessToken,
        });
      } catch {
        // FR-019: swallow. The `finally` below still marks this session attempted, so a first
        // failure does not retry-loop within one session — but it DOES retry on the next app
        // open, because `attemptedThisSession` is reset by a fresh page load.
      } finally {
        attemptedThisSession = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading]);
}
